import * as React from 'react';
import { FormikProps } from 'formik';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { Label } from '../ui/label';
import { useTranslation } from 'react-i18next';

interface BaseInputFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  formik?: FormikProps<any>;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  icon?: React.ReactNode;
  containerClassName?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

interface TextInputFieldProps extends BaseInputFieldProps {
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search';
  maxLength?: number;
  minLength?: number;
}

interface NumberInputFieldProps extends BaseInputFieldProps {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
  value?: number;
}

interface DateInputFieldProps extends BaseInputFieldProps {
  type: 'date' | 'datetime-local' | 'time';
  min?: string;
  max?: string;
  value?: string;
}

interface FileInputFieldProps extends BaseInputFieldProps {
  type: 'file';
  accept?: string;
  multiple?: boolean;
  value?: never;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

type InputFieldProps =
  | TextInputFieldProps
  | NumberInputFieldProps
  | DateInputFieldProps
  | FileInputFieldProps;

const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  placeholder,
  type = 'text',
  className,
  disabled = false,
  required = false,
  helperText,
  formik,
  value,
  onChange,
  onBlur,
  error,
  icon,
  containerClassName,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const isUsingFormik = !!formik;
  const [showPassword, setShowPassword] = React.useState(false);

  const fieldValue = isUsingFormik ? formik.values[name] : value;
  const fieldError = isUsingFormik
    ? formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined
    : error;
  const hasError = !!fieldError;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUsingFormik) {
      formik.handleChange(event);
    }
    if (onChange) {
      onChange(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isUsingFormik) {
      formik.handleBlur(event);
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUsingFormik) {
      const file = event.currentTarget.files?.[0] || null;
      formik.setFieldValue(name, file);
    }
    if (onChange) {
      onChange(event);
    }
  };

  const inputChangeHandler = type === 'file' ? handleFileChange : handleChange;
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div className={cn('space-y-1', containerClassName)}>
      {label && (
        <Label
          htmlFor={name}
          className={cn(
            'text-[0.9375rem] font-semibold leading-[100%] peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
            required && "after:content-['*'] after:ml-0.5 after:text-red-500"
          )}
        >
          {label?.toLowerCase()?.includes('(optional)') ? (
            <>
              {label.replace('(Optional)', '').trim()}{' '}
              <span className="font-normal text-[0.875rem]">
                {t('common.optionalParenthesized')}
              </span>
            </>
          ) : (
            label
          )}
        </Label>
      )}

      <div className="relative">
        {icon && (
          <div className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2">
            {icon}
          </div>
        )}
        <Input
          {...inputProps}
          id={name}
          name={name}
          type={inputType}
          placeholder={placeholder}
          disabled={disabled}
          value={type === 'file' ? undefined : fieldValue}
          onChange={inputChangeHandler}
          onBlur={handleBlur}
          className={cn(
            'h-10 rounded w-full border-none shadow-none bg-background hover:ring-1 ring-primary font-semibold text-primary-black placeholder:font-normal',
            icon && 'pl-10',
            isPasswordField && 'pr-10',
            className,
            'focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-primary',
            hasError &&
              'border border-red-500 ring-red-500 hover:ring-red-500 focus-visible:ring-red-500 focus-visible:ring-offset-0'
          )}
        />
        {isPasswordField && (
          <button
            type="button"
            className="absolute text-gray-500 transition-colors -translate-y-1/2 right-3 top-1/2 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={disabled}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}

      {helperText && !fieldError && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
};

InputField.displayName = 'InputField';

export { InputField };
export type {
  InputFieldProps,
  TextInputFieldProps,
  NumberInputFieldProps,
  DateInputFieldProps,
  FileInputFieldProps,
};
